import React, { useCallback } from "react";
import { darkTheme } from "@yleisradio/yds-core";
import { Forward10, Pause, Play, Replay10 } from "@yleisradio/yds-icons-react";
import styled, { css } from "styled-components";

import { useEmbedParametersContext } from "../../contexts/embedParametersContext";
import { UI_SIZE } from "../../enums";
import i18n from "../../service/i18n";
import { usePlayerStore } from "../../store/usePlayerStore";
import { fontFamily } from "../../styles/global";
import { Timeline } from "../Timeline/Timeline";

import { ControlBarButton } from "./ControlBarButton";
import { Fullscreen } from "./Fullscreen";
import { PictureInPicture } from "./PictureInPicture";
import Settings from "./Settings";
import { Volume } from "./Volume";

const ControlBar = styled.div<{
  controlsVisible: boolean;
  isMobile: boolean;
}>`
  align-items: ${({ isMobile }) => (isMobile ? "center" : "normal")};
  background: linear-gradient(180deg, rgb(0 0 0 / 0%) 0%, #000 100%);
  bottom: ${({ controlsVisible, isMobile }) =>
    controlsVisible ? "0px" : isMobile ? "-50px" : "-96px"};
  color: ${darkTheme.TEXT_DEFAULT};
  display: flex;
  flex-direction: ${({ isMobile }) => (isMobile ? "row" : "column")};
  left: 0;
  padding: ${({ isMobile }) => (isMobile ? "8px;" : "16px 20px;")};
  position: absolute;
  right: 0;
  transition: bottom 0.3s ease-in-out;
  z-index: 10;
  ${fontFamily}

  ${({ isMobile }) =>
    isMobile &&
    css`
      > *:not(:last-child) {
        margin-right: 8px;
      }
    `}
`;

const BottomControlBarButtons = styled.div`
  display: flex;
  justify-content: space-between;
`;

const LeftControls = styled.div`
  display: flex;

  > * {
    margin-right: 16px;
  }
`;

const RightControls = styled.div`
  display: flex;

  > *:not(:last-child) {
    margin-right: 16px;
  }
`;

export const BottomControlBar = () => {
  const { getState, useStoreState } = usePlayerStore();
  const device = useStoreState.device();
  const isControlsVisible = useStoreState.isControlsVisible();
  const isPaused = useStoreState.isPaused();
  const player = useStoreState.player();
  const uiSize = useStoreState.uiSize();

  const { language, pictureInPicture } = useEmbedParametersContext();
  const labelSeekBack = i18n("kelaa_10s_taaksepain", { language });
  const labelSeekForward = i18n("kelaa_10s_eteenpain", { language });

  const seekBackward = useCallback(() => {
    if (getState().currentTime > 0) {
      player.seekFor(-10);
    }
  }, [player, getState]);

  const seekForward = useCallback(() => {
    if (!getState().isEnded) {
      player.seekFor(10);
    }
  }, [player, getState]);

  const labelPlayPause = i18n(
    isPaused ? "toista_pikanap" : "keskeyta_pikanap",
    {
      language
    }
  );

  return (
    <ControlBar controlsVisible={isControlsVisible} isMobile={device.isMobile}>
      {device.isMobile ? (
        <>
          <Timeline />
          <Volume />
          <Fullscreen />
        </>
      ) : (
        <>
          <Timeline />
          <BottomControlBarButtons>
            <LeftControls>
              <ControlBarButton
                aria-keyshortcuts="k"
                aria-label={labelPlayPause}
                onClick={() => (isPaused ? player.play() : player.pause())}
                tooltip={labelPlayPause}>
                {isPaused ? <Play /> : <Pause />}
              </ControlBarButton>
              <ControlBarButton
                aria-label={labelSeekBack}
                onClick={seekBackward}
                tooltip={labelSeekBack}>
                <Replay10 />
              </ControlBarButton>
              <ControlBarButton
                aria-label={labelSeekForward}
                onClick={seekForward}
                tooltip={labelSeekForward}>
                <Forward10 />
              </ControlBarButton>
              <Volume />
            </LeftControls>
            <RightControls>
              {uiSize !== UI_SIZE.XSMALL && (
                <Settings
                  icon="controlsSettingsIconText"
                  settingsName="desktop-texts"
                />
              )}
              {uiSize !== UI_SIZE.XSMALL && (
                <Settings
                  icon="controlsSettingsIconCog"
                  settingsName="desktop-settings"
                />
              )}
              {pictureInPicture &&
                document.pictureInPictureEnabled &&
                uiSize !== UI_SIZE.XSMALL && <PictureInPicture />}
              <Fullscreen />
            </RightControls>
          </BottomControlBarButtons>
        </>
      )}
    </ControlBar>
  );
};
